import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';

interface RequireRoleProps {
  role: string;
  children: ReactNode;
}

/**
 * Route-level role gate, rendered INSIDE `AuthGate` (see AppShell) so by the
 * time this mounts, `useAuthStore.role` already reflects a verified session.
 *
 * `AuthGate` itself only gates on "no valid session" / waitingList / denied
 * — a `role: 'user'` account with `creationState: 'granted'` sails through
 * it the same as a `role: 'admin'` one, since role and creationState are
 * orthogonal concerns there. This component adds the missing "granted, but
 * not the required role" case for role-restricted pages (currently just
 * `/app/admin`) — kept as a route-level wrapper rather than a check inside
 * the page itself, so pages like AdminPanel stay unaware of auth entirely.
 */
export function RequireRole({ role, children }: RequireRoleProps) {
  const currentRole = useAuthStore((state) => state.role);

  if (currentRole !== role) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}
